import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/original/hero-shade.jpg";
import heroPicture from "@/assets/original/hero-shade.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
// Page-specific share image: a real pool & spa cover/upholstery shot — avoids
// falling back to the homepage hero in Facebook / Twitter previews.
import shareImage from "@/assets/pool-upholstery.jpg";

export const Route = createFileRoute("/pool-spa")({
  head: (ctx) => ({
      links: [canonicalLink(pathFromMatch(ctx))],
      meta: routeMeta(ctx, {
        title: "Pool & Spa Covers, Cabanas & Shade | Texas Made Shade",
        description: "Custom pool & spa covers, deck shade, cabanas, and pool sail shades — built in Seguin, TX for the Texas heat.",
        image: shareImage,
        imageAlt: "Custom pool & spa shade and Sunbrella upholstery by Texas Made Shade",
        socialTitle: "Pool & Spa Shade & Covers | Texas Made Shade",
        socialDescription: "Cabanas, sail shades, and custom pool & spa covers.",
      }),
      scripts: serviceHeadScripts({
        path: "/pool-spa",
        serviceName: "Pool & Spa Covers",
        description: "Custom pool & spa covers, deck shade, cabanas, and pool sail shades — built in Seguin, TX for the Texas heat.",
        faqs: [
          { q: "Will a custom pool cover hold up to chlorine and sun?", a: "Yes — we build with marine-grade aluminum hardware and UV-stabilized fabric specifically rated for chlorinated splash zones and direct Texas sun." },
          { q: "Do you build sail shades over swimming pools?", a: "Yes — tension shade sails are one of our most-requested pool projects. We engineer post layout, pour footings and tension the shade for maximum coverage without center supports." },
          { q: "Do you make custom-fit covers for in-ground pools and hot tubs?", a: "We pattern and fabricate custom safety, winter and debris covers for in-ground pools, hot tubs and swim spas." },
        ],
      }),
    }),
  component: () => (
    <ServicePage
      currentPath="/pool-spa"
      serviceName="Pool & Spa Covers"
      eyebrow="★ POOL & SPA ★"
      title="Pool & Spa Shade Structures & Custom Covers"
      intro="Cabanas, deck shade, sail shades over pools, and custom-fit safety and winter covers — all built to handle chlorinated environments and Texas sun."
      heroImage={heroPicture}
      features={[
        "Marine-grade hardware for chlorinated environments",
        "UV-blocking fabric (up to 95% UV block)",
        "Cabanas with welded aluminum frames",
        "Custom-fit pool & spa covers",
        "Deck shade and lounge shade structures",
        "Hot tub and swim spa enclosures",
      ]}
      body={[
        { heading: "Pool Cabanas & Pavilions", text: "Welded aluminum frame cabanas with canvas tops — built poolside and engineered for splash-zone durability." },
        { heading: "Pool Sail Shades", text: "Large-span shade sails over the pool deck for swimmers, sunbathers, and seating areas." },
        { heading: "Custom Pool & Spa Covers", text: "Patterned covers for in-ground pools, hot tubs, and swim spas — heat-retention, debris control, and safety options." },
      ]}
      applications={["Backyard Pools", "Hot Tubs", "Swim Spas", "Public Pools", "Splash Pads", "Resort Pool Decks"]}
      faqs={[
        { q: "Will a custom pool cover hold up to chlorine and sun?", a: "Yes — we build with marine-grade aluminum hardware and UV-stabilized fabric specifically rated for chlorinated splash zones and direct Texas sun." },
        { q: "Do you build sail shades over swimming pools?", a: "Absolutely. Tension shade sails are one of our most-requested pool projects. We engineer the post layout, pour footings, and tension the shade for maximum coverage without center supports." },
        { q: "Can you build a cabana with an outdoor kitchen?", a: "Yes. Welded aluminum cabanas with canvas or metal tops are a specialty — including provisions for grills, fans, lighting, and outdoor kitchens." },
        { q: "Do you make custom-fit covers for in-ground pools and hot tubs?", a: "We pattern and fabricate custom safety, winter, and debris covers for in-ground pools, hot tubs, and swim spas." },
      ]}
    />
  ),
});
